import React, { useEffect, useRef, useState } from 'react';
import { useWindowSize } from 'common/hooks';
import { optimizeContentfulImageUrl } from 'common/components';

function useAssetField(asset) {
  const { isMobile, isTablet, isDesktop } = useWindowSize();

  if (asset?.file) return { fields: asset };

  if (isMobile) return asset?.mobile;
  if (isTablet) return asset?.tablet;
  if (isDesktop) return asset?.desktop;

  return null;
}

function Image({ image, className, style, ...props }) {
  return (
    <img
      src={optimizeContentfulImageUrl(
        image.fields.file.url,
        image.fields.file.contentType
      )}
      alt={image.fields.title}
      style={style}
      className={className}
      {...props}
    />
  );
}

function Video({
  video,
  poster,
  videoProps,
  style = {},
  videoStyle = {},
  className,
  videoEvents,
  ...props
}) {
  const videoRef = useRef(null);
  const field = useAssetField(poster?.fields);
  const [canAutoplay, setCanAutoplay] = useState(true);
  const isSafari = navigator.userAgent.toLowerCase().includes('safari');
  const shouldAutoplay = videoEvents?.autoplay !== false;

  const {
    autoplay = true,
    loop = true,
    muted = true,
    controls = false,
    playsInline = true,
    preload,
  } = videoProps || {};

  useEffect(() => {
    const current = videoRef.current;
    if (!current) return;
    if (muted) current.defaultMuted = true;
    if (!shouldAutoplay || !autoplay) {
      current.pause();
      return;
    }
    if (shouldAutoplay) {
      current.play().catch(() => setCanAutoplay(false));
    }
  }, [shouldAutoplay, autoplay, muted]);

  if (shouldAutoplay && !canAutoplay && isSafari) {
    return (
      <div
        className={`autoplay ${className}`}
        style={{
          backgroundImage: `url(${video.fields.file.url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: '.6',
          ...style,
          ...videoStyle,
        }}
        {...props}
      />
    );
  }

  return (
    <video
      src={video.fields.file.url}
      poster={optimizeContentfulImageUrl(
        field?.fields?.file?.url,
        field?.fields?.file?.contentType
      )}
      autoPlay={autoplay}
      loop={loop}
      muted={muted}
      controls={controls}
      playsInline={playsInline}
      {...(preload && { preload })}
      ref={videoRef}
      style={{ opacity: 0.6, ...style, ...videoStyle }}
      className={className}
      onMouseEnter={() => {
        if (videoEvents?.playOnHover) {
          videoRef.current?.play().catch(() => {});
        }
      }}
      onMouseLeave={() => {
        const v = videoRef.current;
        if (!v) return;
        if (videoEvents?.stopOnLeave) {
          v.pause();
          v.currentTime = 0;
        } else if (videoEvents?.pauseOnLeave) {
          v.pause();
        }
      }}
      {...props}
    />
  );
}

export default function HomepageAsset({
  asset,
  poster,
  videoProps,
  style,
  videoStyle,
  className,
  videoEvents,
  ...props
}) {
  const field = useAssetField(asset?.fields);
  if (!field || !field.fields?.file) return null;

  if (field.fields.file.contentType.startsWith('image')) {
    return (
      <Image image={field} className={className} style={style} {...props} />
    );
  }

  return (
    <Video
      video={field}
      poster={poster}
      videoProps={videoProps}
      style={style}
      videoStyle={videoStyle}
      className={className}
      videoEvents={videoEvents}
      {...props}
    />
  );
}
